import React, { useState, useEffect, useMemo, useCallback } from "react";
import {
  FileText,
  LayoutGrid,
  Users,
  AlertTriangle,
  Search,
  X,
  Briefcase,
  Layers,
  Filter,
  Settings,
  Sun,
  Moon,
  ChevronUp,
  ChevronDown,
  Info,
} from "lucide-react";
import { useSearchDebounce } from "../hooks/useDebounce";
import {
  createProjectSearchFilter,
  createClientSearchFilter,
  createTakeoffSearchFilter
} from "../utils/searchUtils";
import OfficeAdminCard from "./OfficeAdminCard";
import AllJobsTable from "./AllJobsTable";
import AllClientsTable from "./AllClientsTable";
import TakeoffManagementTable from "./TakeoffManagementTable";
import TakeoffCard from "./TakeoffCard";
import OfficeAdminModal from "./OfficeAdminModal";
import AgreementReadyModal from "./AgreementReadyModal";
import ClientModal from "./ClientModal";
import TakeoffManagementModal from "./TakeoffManagementModal";
import MultiSelectDropdown from "./MultiSelectDropdown";
import CustomFieldFilter from "./CustomFieldFilter";
import LoadingSpinner from "./LoadingSpinner";
import ErrorMessage from "./ErrorMessage";
import { useDataContext } from "../contexts/DataContext";
import { Button, Popover, SearchBar } from "./ui";

const OfficeAdminDashboard = () => {
  // Use global context for data
  const {
    projects,
    clients,
    clientsWithBilling,
    contacts,
    employees,
    takeoffs,
    loading,
    error,
    loadAllData
  } = useDataContext();

  // Theme state management (only for Office Admin Board)
  const [theme, setTheme] = useState(() => {
    const savedTheme = localStorage.getItem("office-admin-theme");
    return savedTheme || "dark";
  });

  // Persist theme changes to localStorage
  useEffect(() => {
    localStorage.setItem("office-admin-theme", theme);
  }, [theme]);

  // Toggle theme function
  const toggleTheme = () => {
    setTheme(prevTheme => prevTheme === "dark" ? "light" : "dark");
  };

  // Local state for UI interactions only
  const [selectedProject, setSelectedProject] = useState(null);
  const [selectedClient, setSelectedClient] = useState(null);
  const [selectedTakeoff, setSelectedTakeoff] = useState(null);
  const [selectedTakeoffManagement, setSelectedTakeoffManagement] = useState(null);
  const [modalOpen, setModalOpen] = useState(false);
  const [isTakeoffManagementModalOpen, setIsTakeoffManagementModalOpen] = useState(false);
  const [modalType, setModalType] = useState("project"); // "project", "client", or "takeoff"
  const [modalSource, setModalSource] = useState(null); // Track which section opened the modal
  const [activeTab, setActiveTab] = useState("action-items");
  // Filter states for All Projects tab - now arrays for multi-select
  const [modelingStatusFilter] = useState([]);
  const [agreementStatusFilter] = useState([]);
  const [billingStatusFilter] = useState([]);
  const [clientFilter] = useState([]);
  // Filter states for All Clients tab
  const [clientBillingStatusFilter, setClientBillingStatusFilter] = useState(
    [],
  );
  const [clientStatusFilter, setClientStatusFilter] = useState([]);
  const [clientOfFilter, setClientOfFilter] = useState([]);
  // Custom field filters for All Clients tab
  const [customFilters, setCustomFilters] = useState([]);
  // Search state for All Projects - using debounced search
  const {
    debouncedSearchQuery: debouncedProjectSearch
  } = useSearchDebounce("", 300);

  // Search state for All Clients - using debounced search
  const {
    searchQuery: clientSearchQuery,
    debouncedSearchQuery: debouncedClientSearch,
    isSearching: isClientSearching,
    updateSearch: updateClientSearch,
    clearSearch: clearClientSearch
  } = useSearchDebounce("", 300);
  // Sorting state for All Projects
  const [projectSortColumn] = useState('');
  const [projectSortDirection] = useState('asc');
  // Column visibility menu states
  const [showTakeoffManagementColumnMenu, setShowTakeoffManagementColumnMenu] = useState(false);

  // Filter and Column popover states for each tab
  const [showClientsFilters, setShowClientsFilters] = useState(false);
  const [showClientsColumns, setShowClientsColumns] = useState(false);
  const [showTakeoffFilters, setShowTakeoffFilters] = useState(false);
  const [showTakeoffColumns, setShowTakeoffColumns] = useState(false);
  const [showJobsFilters, setShowJobsFilters] = useState(false);
  const [showJobsColumns, setShowJobsColumns] = useState(false);

  // Auto-resize states for each tab
  const [clientsAutoResize, setClientsAutoResize] = useState(true);
  const [takeoffAutoResize, setTakeoffAutoResize] = useState(true);
  const [jobsAutoResize, setJobsAutoResize] = useState(true);

  // Pagination states for Action Items sections (show 20 initially)
  const [showAllReadyToInvoiceABCivil, setShowAllReadyToInvoiceABCivil] = useState(false);
  const [showAllReadyToInvoiceABCivilNC, setShowAllReadyToInvoiceABCivilNC] = useState(false);
  const [showAllAgreementReady, setShowAllAgreementReady] = useState(false);
  const [showAllReadyToInvoiceRemoteServices, setShowAllReadyToInvoiceRemoteServices] = useState(false);
  const [showAllReadyToInvoiceTakeoffs, setShowAllReadyToInvoiceTakeoffs] = useState(false);
  const [showAllCompletedNotInvoiced, setShowAllCompletedNotInvoiced] = useState(false);

  const INITIAL_ITEMS_COUNT = 20;
  
  // Projects table column visibility state - keeping for future use
  // const [projectVisibleColumns, setProjectVisibleColumns] = useState(() => {
  //   const saved = localStorage.getItem("office-admin-projects-columns");
  //   return saved ? JSON.parse(saved).visibleColumns : {};
  // });

  // Client table column visibility state
  const [clientVisibleColumns, setClientVisibleColumns] = useState(() => {
    const saved = localStorage.getItem('all-clients-visible-columns');
    return saved
      ? JSON.parse(saved)
      : {
          action: true,
          name: true,
          billingStatus: true,
          yearlyCost: true,
          clientsInvoicedDate: true,
          clientOf: true,
          clientStatus: true,
          state: true,
          platform: true,
          address: false, // Hidden by default
          phoneNumber: false, // Hidden by default
        };
  });

  // Takeoff table column visibility state
  const [takeoffVisibleColumns, setTakeoffVisibleColumns] = useState(() => {
    const saved = localStorage.getItem("office-admin-takeoff-columns");
    return saved ? JSON.parse(saved).visibleColumns : {};
  });

  // Jobs table column visibility state
  const [jobsVisibleColumns, setJobsVisibleColumns] = useState(() => {
    const saved = localStorage.getItem('all-jobs-visible-columns');
    return saved ? JSON.parse(saved) : {
      action: true,
      jobNumber: true,
      projectName: true,
      clientName: true,
      contactName: true,
      agreementStatus: true,
      billingStatus: true,
      readyToInvoice: false,
      modelPrice: true,
      calculatedPrice: true,
      poNumber: true,
      initialAcceptance: true,
      invoicedDate: true,
      paidDate: true,
      address: false,
      modelingStatus: false
    };
  });


  const handleClientColumnToggle = (columnKey) => {
    const newVisibleColumns = {
      ...clientVisibleColumns,
      [columnKey]: !clientVisibleColumns[columnKey],
    };
    setClientVisibleColumns(newVisibleColumns);
    localStorage.setItem(
      'all-clients-visible-columns',
      JSON.stringify(newVisibleColumns)
    );
  };

  const handleTakeoffColumnToggle = (columnKey) => {
    const newVisibleColumns = {
      ...takeoffVisibleColumns,
      [columnKey]: !takeoffVisibleColumns[columnKey],
    };
    setTakeoffVisibleColumns(newVisibleColumns);
    localStorage.setItem(
      "office-admin-takeoff-columns",
      JSON.stringify({ visibleColumns: newVisibleColumns })
    );
  };

  const handleJobsColumnToggle = (columnKey) => {
    const newVisibleColumns = {
      ...jobsVisibleColumns,
      [columnKey]: !jobsVisibleColumns[columnKey],
    };
    setJobsVisibleColumns(newVisibleColumns);
    localStorage.setItem(
      'all-jobs-visible-columns',
      JSON.stringify(newVisibleColumns)
    );
  };

  // Pagination state for All Projects - keeping for future use
  // const [currentPage] = useState(1);
  // const ITEMS_PER_PAGE = 100;

  // Filter states for Takeoff Invoicing - with default filters
  const [takeoffStatusFilter, setTakeoffStatusFilter] = useState([]);
  const [takeoffInvoicedStatusFilter, setTakeoffInvoicedStatusFilter] =
    useState([]);
  // Search state for Takeoffs - using debounced search
  const {
    searchQuery: takeoffSearchQuery,
    debouncedSearchQuery: debouncedTakeoffSearch,
    isSearching: isTakeoffSearching,
    updateSearch: updateTakeoffSearch,
    clearSearch: clearTakeoffSearch
  } = useSearchDebounce("", 300);

  // Filter states for All Jobs tab
  const [jobsModelingStatusFilter, setJobsModelingStatusFilter] = useState([]);
  const [jobsAgreementStatusFilter, setJobsAgreementStatusFilter] = useState([]);
  const [jobsBillingStatusFilter, setJobsBillingStatusFilter] = useState([]);
  const [jobsStageStatusFilter, setJobsStageStatusFilter] = useState([]);
  const [jobsClientFilter, setJobsClientFilter] = useState([]);

  // Search state for All Jobs - using debounced search
  const {
    searchQuery: jobsSearchQuery,
    debouncedSearchQuery: debouncedJobsSearch,
    isSearching: isJobsSearching,
    updateSearch: updateJobsSearch,
    clearSearch: clearJobsSearch
  } = useSearchDebounce("", 300);

  // Sorting state for All Jobs
  const [jobsSortColumn] = useState('jobNumber');
  const [jobsSortDirection] = useState('asc');

  // Pagination state for All Jobs
  const [jobsCurrentPage, setJobsCurrentPage] = useState(1);
  const JOBS_PER_PAGE = 20;

  // Pagination state for Takeoff Invoicing
  const [takeoffCurrentPage, setTakeoffCurrentPage] = useState(1);
  const TAKEOFF_PER_PAGE = 20;

  // Pagination state for All Clients
  const [clientsCurrentPage, setClientsCurrentPage] = useState(1);
  const CLIENTS_PER_PAGE = 20;

  // Reset pagination when filters change
  useEffect(() => {
    setTakeoffCurrentPage(1);
  }, [takeoffStatusFilter, takeoffInvoicedStatusFilter]);

  useEffect(() => {
    setClientsCurrentPage(1);
  }, [clientBillingStatusFilter, clientStatusFilter, clientOfFilter, customFilters]);

  // Sorting state for Overdue Invoices
  const [overdueInvoicesSortColumn, setOverdueInvoicesSortColumn] = useState('daysOverdue');
  const [overdueInvoicesSortDirection, setOverdueInvoicesSortDirection] = useState('desc');

  // Toggle state for Projects vs Takeoffs
  const [overdueInvoicesShowType, setOverdueInvoicesShowType] = useState('all'); // 'all', 'projects', 'takeoffs'

  // No additional filters for Overdue Invoices - just search

  // Search state for Overdue Invoices - using debounced search
  const {
    searchQuery: overdueSearchQuery,
    debouncedSearchQuery: debouncedOverdueSearch,
    isSearching: isOverdueSearching,
    updateSearch: updateOverdueSearch,
    clearSearch: clearOverdueSearch
  } = useSearchDebounce("", 300);

  // Load data on component mount
  useEffect(() => {
    loadAllData();
  }, [loadAllData]);

  // Get unique values for filters
  const modelingStatusOptions = useMemo(() => {
    const allStatuses = projects.map((p) => p.modelingStatus);
    const nonNullStatuses = allStatuses.filter(Boolean);
    const statuses = new Set(nonNullStatuses);
    const result = Array.from(statuses).sort();
    return result;
  }, [projects]);

  const agreementStatusOptions = useMemo(() => {
    const statuses = new Set(
      projects.map((p) => p.agreementStatus).filter(Boolean),
    );
    return Array.from(statuses).sort();
  }, [projects]);

  const billingStatusOptions = useMemo(() => {
    const statuses = new Set(
      projects.map((p) => p.billingStatus).filter(Boolean),
    );
    return Array.from(statuses).sort();
  }, [projects]);

  const clientOptions = useMemo(() => {
    const clientNames = new Set(
      projects.map((p) => p.clientName).filter(Boolean),
    );
    return Array.from(clientNames).sort();
  }, [projects]);

  // Get unique values for client filters
  const clientBillingStatusOptions = useMemo(() => {
    const statuses = new Set(
      clientsWithBilling.map((c) => c.billing_status).filter(Boolean),
    );
    return Array.from(statuses).sort();
  }, [clientsWithBilling]);

  const clientStatusOptions = useMemo(() => {
    const statuses = new Set(
      clientsWithBilling.map((c) => c.client_status).filter(Boolean),
    );
    return Array.from(statuses).sort();
  }, [clientsWithBilling]);

  const clientOfOptions = useMemo(() => {
    const options = new Set(
      clientsWithBilling.map((c) => c.client_of).filter(Boolean),
    );
    return Array.from(options).sort();
  }, [clientsWithBilling]);

  // Helper function to apply custom filters
  const applyCustomFilter = (client, filter) => {
    const fieldValue = client[filter.field];
    const filterValue = filter.value;

    // Handle array fields (like brand, services_provided, trimble_formats)
    if (Array.isArray(fieldValue)) {
      const arrayString = fieldValue.join(' ').toLowerCase();
      switch (filter.condition) {
        case 'equals':
          return arrayString === filterValue.toLowerCase();
        case 'contains':
          return arrayString.includes(filterValue.toLowerCase());
        case 'not_contains':
          return !arrayString.includes(filterValue.toLowerCase());
        case 'is_empty':
          return fieldValue.length === 0 || fieldValue.every(v => !v || v.trim() === '');
        case 'is_not_empty':
          return fieldValue.length > 0 && fieldValue.some(v => v && v.trim() !== '');
        case 'starts_with':
          return arrayString.startsWith(filterValue.toLowerCase());
        case 'ends_with':
          return arrayString.endsWith(filterValue.toLowerCase());
        case 'not_equals':
          return arrayString !== filterValue.toLowerCase();
        default:
          return true;
      }
    }

    // Handle string/null fields
    const stringValue = fieldValue ? fieldValue.toString().toLowerCase() : '';
    const filterValueLower = filterValue ? filterValue.toLowerCase() : '';

    switch (filter.condition) {
      case 'equals':
        return stringValue === filterValueLower;
      case 'contains':
        return stringValue.includes(filterValueLower);
      case 'not_contains':
        return !stringValue.includes(filterValueLower);
      case 'starts_with':
        return stringValue.startsWith(filterValueLower);
      case 'ends_with':
        return stringValue.endsWith(filterValueLower);
      case 'is_empty':
        return !fieldValue || fieldValue.toString().trim() === '';
      case 'is_not_empty':
        return fieldValue && fieldValue.toString().trim() !== '';
      case 'not_equals':
        return stringValue !== filterValueLower;
      default:
        return true;
    }
  };

  // Filtered clients for All Clients tab - updated with improved search
  const filteredClients = useMemo(() => {
    const searchFilter = createClientSearchFilter(debouncedClientSearch);

    return clientsWithBilling.filter((c) => {
      // Apply search filter using the improved utility
      if (!searchFilter(c)) return false;

      // Standard multi-select filters
      if (
        clientBillingStatusFilter.length > 0 &&
        !clientBillingStatusFilter.includes(c.billing_status)
      )
        return false;
      if (
        clientStatusFilter.length > 0 &&
        !clientStatusFilter.includes(c.client_status)
      )
        return false;
      if (clientOfFilter.length > 0 && !clientOfFilter.includes(c.client_of))
        return false;

      // Custom field filters
      if (customFilters.length > 0) {
        for (const filter of customFilters) {
          if (!applyCustomFilter(c, filter)) {
            return false;
          }
        }
      }

      return true;
    });
  }, [
    clientsWithBilling,
    debouncedClientSearch,
    clientBillingStatusFilter,
    clientStatusFilter,
    clientOfFilter,
    customFilters,
  ]);

  // Filtered takeoffs for Takeoff Invoicing tab - updated with improved search
  const filteredTakeoffs = useMemo(() => {
    const searchFilter = createTakeoffSearchFilter(debouncedTakeoffSearch);

    return takeoffs.filter((t) => {
      // Apply search filter using the improved utility
      if (!searchFilter(t)) return false;

      if (
        takeoffStatusFilter.length > 0 &&
        !takeoffStatusFilter.includes(t.status)
      )
        return false;

      if (
        takeoffInvoicedStatusFilter.length > 0 &&
        !takeoffInvoicedStatusFilter.includes(t.invoicedStatus)
      ) {
        return false;
      }

      return true;
    });
  }, [
    takeoffs,
    debouncedTakeoffSearch,
    takeoffStatusFilter,
    takeoffInvoicedStatusFilter,
  ]);

  // Get unique values for takeoff filters
  const takeoffStatusOptions = useMemo(() => {
    const statuses = new Set(takeoffs.map((t) => t.status).filter(Boolean));
    return Array.from(statuses).sort();
  }, [takeoffs]);

  const takeoffInvoicedStatusOptions = useMemo(() => {
    const statuses = new Set(
      takeoffs.map((t) => t.invoicedStatus).filter(Boolean),
    );
    return Array.from(statuses).sort();
  }, [takeoffs]);

  // Filtered projects for All Jobs tab
  const filteredJobs = useMemo(() => {
    const searchFilter = createProjectSearchFilter(debouncedJobsSearch);

    return projects.filter((p) => {
      // Apply search filter
      if (!searchFilter(p)) return false;

      // Apply modeling status filter
      if (
        jobsModelingStatusFilter.length > 0 &&
        !jobsModelingStatusFilter.includes(p.modelingStatus)
      ) return false;

      // Apply agreement status filter
      if (
        jobsAgreementStatusFilter.length > 0 &&
        !jobsAgreementStatusFilter.includes(p.agreementStatus)
      ) return false;

      // Apply billing status filter
      if (
        jobsBillingStatusFilter.length > 0 &&
        !jobsBillingStatusFilter.includes(p.billingStatus)
      ) return false;

      // Apply stage status filter
      if (
        jobsStageStatusFilter.length > 0 &&
        !jobsStageStatusFilter.includes(p.stageStatus)
      ) return false;

      // Apply client filter
      if (
        jobsClientFilter.length > 0 &&
        !jobsClientFilter.includes(p.clientName)
      ) return false;

      return true;
    });
  }, [
    projects,
    debouncedJobsSearch,
    jobsModelingStatusFilter,
    jobsAgreementStatusFilter,
    jobsBillingStatusFilter,
    jobsStageStatusFilter,
    jobsClientFilter,
  ]);

  // Get unique stage status values
  const stageStatusOptions = useMemo(() => {
    const statuses = new Set(projects.map((p) => p.stageStatus).filter(Boolean));
    return Array.from(statuses).sort();
  }, [projects]);

  // Helper function to sort projects
  const getSortedProjects = useCallback((projectsToSort, sortColumn = projectSortColumn, sortDirection = projectSortDirection) => {
    if (!sortColumn) return projectsToSort;

    return [...projectsToSort].sort((a, b) => {
      let aValue = a[sortColumn];
      let bValue = b[sortColumn];

      // Handle special cases for different data types
      if (sortColumn === 'modelPrice' || sortColumn === 'calculatedPrice') {
        // Handle price fields - parse numbers from currency strings
        aValue = aValue ? parseFloat(String(aValue).replace(/[^0-9.-]/g, '')) : 0;
        bValue = bValue ? parseFloat(String(bValue).replace(/[^0-9.-]/g, '')) : 0;
      } else if (sortColumn === 'invoiceDate' || sortColumn === 'paidDate') {
        // Handle date fields
        aValue = aValue ? new Date(aValue).getTime() : 0;
        bValue = bValue ? new Date(bValue).getTime() : 0;
      } else if (sortColumn === 'jobNumber') {
        // Handle job numbers with numeric sorting
        const aNum = aValue ? parseInt(String(aValue).match(/^(\d+)/)?.[1] || "0") : 0;
        const bNum = bValue ? parseInt(String(bValue).match(/^(\d+)/)?.[1] || "0") : 0;
        aValue = aNum;
        bValue = bNum;
      } else {
        // Handle string fields
        aValue = aValue ? String(aValue).toLowerCase() : '';
        bValue = bValue ? String(bValue).toLowerCase() : '';
      }

      // Sort nulls/empty values to the end
      if (!aValue && !bValue) return 0;
      if (!aValue) return 1;
      if (!bValue) return -1;

      let result = 0;
      if (aValue < bValue) result = -1;
      else if (aValue > bValue) result = 1;

      return sortDirection === 'desc' ? -result : result;
    });
  }, [projectSortColumn, projectSortDirection]);

  // Apply sorting to filtered jobs
  const sortedJobs = useMemo(() => {
    return getSortedProjects(filteredJobs, jobsSortColumn, jobsSortDirection);
  }, [filteredJobs, jobsSortColumn, jobsSortDirection, getSortedProjects]);

  // Apply pagination to sorted jobs
  const paginatedJobs = useMemo(() => {
    const startIdx = (jobsCurrentPage - 1) * JOBS_PER_PAGE;
    const endIdx = startIdx + JOBS_PER_PAGE;
    return sortedJobs.slice(startIdx, endIdx);
  }, [sortedJobs, jobsCurrentPage]);

  // Calculate pagination info for jobs
  const jobsTotalPages = Math.ceil(sortedJobs.length / JOBS_PER_PAGE);
  const jobsShowingStart = sortedJobs.length === 0 ? 0 : (jobsCurrentPage - 1) * JOBS_PER_PAGE + 1;
  const jobsShowingEnd = Math.min(jobsCurrentPage * JOBS_PER_PAGE, sortedJobs.length);

  // Apply pagination to takeoffs
  const paginatedTakeoffs = useMemo(() => {
    const startIdx = (takeoffCurrentPage - 1) * TAKEOFF_PER_PAGE;
    const endIdx = startIdx + TAKEOFF_PER_PAGE;
    return filteredTakeoffs.slice(startIdx, endIdx);
  }, [filteredTakeoffs, takeoffCurrentPage]);

  // Calculate pagination info for takeoffs
  const takeoffTotalPages = Math.ceil(filteredTakeoffs.length / TAKEOFF_PER_PAGE);
  const takeoffShowingStart = filteredTakeoffs.length === 0 ? 0 : (takeoffCurrentPage - 1) * TAKEOFF_PER_PAGE + 1;
  const takeoffShowingEnd = Math.min(takeoffCurrentPage * TAKEOFF_PER_PAGE, filteredTakeoffs.length);

  // Apply pagination to clients
  const paginatedClients = useMemo(() => {
    const startIdx = (clientsCurrentPage - 1) * CLIENTS_PER_PAGE;
    const endIdx = startIdx + CLIENTS_PER_PAGE;
    return filteredClients.slice(startIdx, endIdx);
  }, [filteredClients, clientsCurrentPage]);

  // Calculate pagination info for clients
  const clientsTotalPages = Math.ceil(filteredClients.length / CLIENTS_PER_PAGE);
  const clientsShowingStart = filteredClients.length === 0 ? 0 : (clientsCurrentPage - 1) * CLIENTS_PER_PAGE + 1;
  const clientsShowingEnd = Math.min(clientsCurrentPage * CLIENTS_PER_PAGE, filteredClients.length);

  // Filtered projects for All Projects tab - updated with improved search
  // eslint-disable-next-line no-unused-vars
  const filteredProjects = useMemo(() => {

    const searchFilter = createProjectSearchFilter(debouncedProjectSearch);

    let filterStats = {
      bySearch: 0,
      byModelingStatus: 0,
      byAgreementStatus: 0,
      byBillingStatus: 0,
      byClient: 0,
      passed: 0
    };

    const filtered = projects.filter((p, index) => {
      // Apply search filter using the improved utility
      const searchResult = searchFilter(p);
      if (!searchResult) {
        filterStats.bySearch++;
        return false;
      }

      // Apply other filters - NOTE: Empty filter arrays mean "show all"
      // Only filter if the array has selections
      if (
        modelingStatusFilter.length > 0 &&
        !modelingStatusFilter.includes(p.modelingStatus)
      ) {
        filterStats.byModelingStatus++;
        return false;
      }
      if (
        agreementStatusFilter.length > 0 &&
        !agreementStatusFilter.includes(p.agreementStatus)
      ) {
        filterStats.byAgreementStatus++;
        return false;
      }
      if (
        billingStatusFilter.length > 0 &&
        !billingStatusFilter.includes(p.billingStatus)
      ) {
        filterStats.byBillingStatus++;
        return false;
      }
      if (
        clientFilter.length > 0 &&
        !clientFilter.includes(p.clientName)
      ) {
        filterStats.byClient++;
        return false;
      }
      filterStats.passed++;
      return true;
    });

    return filtered;
  }, [
    projects,
    debouncedProjectSearch,
    modelingStatusFilter,
    agreementStatusFilter,
    billingStatusFilter,
    clientFilter,
  ]);


  // Apply sorting and pagination to filtered projects - keeping for future use
  // const sortedProjects = useMemo(() => {
  //   const sorted = getSortedProjects(filteredProjects);
  //   return sorted;
  // }, [filteredProjects, getSortedProjects]);


  // No pagination needed - showing all projects

  const readyToInvoiceABCivil = useMemo(() => {

    const filtered = projects
      .filter((p) => {
        const isReadyToInvoice = p.readyToInvoice === "Yes";
        const billingNotComplete = !["Paid", "Voided", "Invoiced"].includes(
          p.billingStatus,
        );
        const isABCivilOrEmpty = p.clientOf === "AB Civil" || !p.clientOf;


        return isReadyToInvoice && billingNotComplete && isABCivilOrEmpty;
      })
      .sort((a, b) => {
        const aNum = a.jobNumber
          ? parseInt(a.jobNumber.match(/^(\d+)/)?.[1] || "0")
          : 0;
        const bNum = b.jobNumber
          ? parseInt(b.jobNumber.match(/^(\d+)/)?.[1] || "0")
          : 0;
        return bNum - aNum;
      });

    return filtered;
  }, [projects]);

  const readyToInvoiceRemoteServices = useMemo(() => {
    const filtered = clientsWithBilling
      .filter((client) => {
        return client.billing_status === "Ready to Invoice";
      })
      .sort((a, b) => {
        // Sort by name alphabetically
        return (a.name || "").localeCompare(b.name || "");
      });

    return filtered;
  }, [clientsWithBilling]);

  const readyToInvoiceABCivilNC = useMemo(() => {
    const filtered = projects
      .filter((p) => {
        const isReadyToInvoice = p.readyToInvoice === "Yes";
        const billingNotComplete = !["Paid", "Voided", "Invoiced"].includes(
          p.billingStatus,
        );
        const isABCivilNC = p.clientOf === "AB Civil NC";

        return isReadyToInvoice && billingNotComplete && isABCivilNC;
      })
      .sort((a, b) => {
        const aNum = a.jobNumber
          ? parseInt(a.jobNumber.match(/^(\d+)/)?.[1] || "0")
          : 0;
        const bNum = b.jobNumber
          ? parseInt(b.jobNumber.match(/^(\d+)/)?.[1] || "0")
          : 0;
        return bNum - aNum;
      });

    return filtered;
  }, [projects]);

  const completedNotInvoiced = useMemo(() => {
    const filtered = projects
      .filter((p) => {
        const isCompleted = p.modelingStatus === "Completed";
        const readyToInvoiceEmpty =
          p.readyToInvoice === null ||
          p.readyToInvoice === undefined ||
          p.readyToInvoice === "" ||
          p.readyToInvoice === "Not Set";

        return isCompleted && readyToInvoiceEmpty;
      })
      .sort((a, b) => {
        const aNum = a.jobNumber
          ? parseInt(a.jobNumber.match(/^(\d+)/)?.[1] || "0")
          : 0;
        const bNum = b.jobNumber
          ? parseInt(b.jobNumber.match(/^(\d+)/)?.[1] || "0")
          : 0;
        return bNum - aNum;
      });

    return filtered;
  }, [projects]);

  const agreementReady = useMemo(() => {
    const filtered = projects
      .filter((p) => {
        const hasReadyAgreement = ["Ready", "Utility Ready"].includes(
          p.agreementStatus,
        );

        return hasReadyAgreement;
      })
      .sort((a, b) => {
        const aNum = a.jobNumber
          ? parseInt(a.jobNumber.match(/^(\d+)/)?.[1] || "0")
          : 0;
        const bNum = b.jobNumber
          ? parseInt(b.jobNumber.match(/^(\d+)/)?.[1] || "0")
          : 0;
        return bNum - aNum;
      });

    return filtered;
  }, [projects]);

  const readyToInvoiceTakeoffs = useMemo(() => {
    return takeoffs
      .filter((takeoff) => {
        const isCompleted = takeoff.status === "Completed";
        const notInvoiced = !takeoff.invoicedStatus || 
                            takeoff.invoicedStatus === "";
        return isCompleted && notInvoiced;
      })
      .sort((a, b) => {
        // Sort by job number descending (newest first)
        const aNum = a.jobNumber && typeof a.jobNumber === 'string'
          ? parseInt(a.jobNumber.match(/^(\d+)/)?.[1] || "0") 
          : 0;
        const bNum = b.jobNumber && typeof b.jobNumber === 'string'
          ? parseInt(b.jobNumber.match(/^(\d+)/)?.[1] || "0") 
          : 0;
        return bNum - aNum;
      });
  }, [takeoffs]);

  // Calculate days overdue for a given invoice date
  const calculateDaysOverdue = (invoiceDate) => {
    if (!invoiceDate) return null;
    const today = new Date();
    const invoiced = new Date(invoiceDate);
    const diffTime = today - invoiced;
    const days = Math.floor(diffTime / (1000 * 60 * 60 * 24));
    return days; // Return the actual days, even if negative (not yet overdue)
  };

  // Handle sorting for overdue invoices
  const handleOverdueInvoicesSort = (column) => {
    if (overdueInvoicesSortColumn === column) {
      // Toggle direction if same column
      setOverdueInvoicesSortDirection(overdueInvoicesSortDirection === 'asc' ? 'desc' : 'asc');
    } else {
      // New column, start with ascending (except for daysOverdue which defaults to desc)
      setOverdueInvoicesSortColumn(column);
      setOverdueInvoicesSortDirection(column === 'daysOverdue' ? 'desc' : 'asc');
    }
  };


  // Overdue invoices calculations
  const overdueInvoices = useMemo(() => {
    const overdueProjects = projects
      .filter((p) => {
        // Apply type filter (projects vs takeoffs)
        if (overdueInvoicesShowType !== 'all') {
          // Assuming job numbers < 1000 are takeoffs, >= 1000 are projects
          // This is a common pattern - adjust based on your actual business rules
          const jobNum = parseInt(p.jobNumber) || 0;
          const isTakeoff = jobNum > 0 && jobNum < 1000;

          if (overdueInvoicesShowType === 'projects' && isTakeoff) return false;
          if (overdueInvoicesShowType === 'takeoffs' && !isTakeoff) return false;
        }

        // Must be invoiced (not paid, not voided)
        const isInvoiced = p.billingStatus === "Invoiced";
        const hasInvoiceDate = p.invoiceDate || p.invoicedDate;

        if (!isInvoiced || !hasInvoiceDate) return false;

        const daysOverdue = calculateDaysOverdue(
          p.invoiceDate || p.invoicedDate,
        );

        // Check for null values and only include if truly overdue
        return daysOverdue !== null && daysOverdue >= 45; // 45+ days overdue
      })
      .map((p) => ({
        ...p,
        daysOverdue: calculateDaysOverdue(p.invoiceDate || p.invoicedDate),
      }))
      .filter((p) => {
        // Apply search filter
        if (debouncedOverdueSearch) {
          const searchLower = debouncedOverdueSearch.toLowerCase();
          const matches =
            (p.jobNumber && p.jobNumber.toLowerCase().includes(searchLower)) ||
            (p.projectName && p.projectName.toLowerCase().includes(searchLower)) ||
            (p.clientName && p.clientName.toLowerCase().includes(searchLower)) ||
            (p.poNumber && p.poNumber.toLowerCase().includes(searchLower)) ||
            (p.address && p.address.toLowerCase().includes(searchLower));

          if (!matches) return false;
        }

        return true;
      })
      .sort((a, b) => {
        // Apply sorting based on selected column
        let aValue, bValue;

        switch (overdueInvoicesSortColumn) {
          case 'jobNumber':
            // Extract numeric part of job number for proper sorting
            aValue = a.jobNumber ? parseInt(a.jobNumber.match(/^(\d+)/)?.[1] || "0") : 0;
            bValue = b.jobNumber ? parseInt(b.jobNumber.match(/^(\d+)/)?.[1] || "0") : 0;
            break;
          case 'clientName':
            aValue = (a.clientName || '').toLowerCase();
            bValue = (b.clientName || '').toLowerCase();
            break;
          case 'daysOverdue':
          default:
            aValue = a.daysOverdue;
            bValue = b.daysOverdue;
            break;
        }

        // Handle null/empty values
        if (!aValue && !bValue) return 0;
        if (!aValue) return 1;
        if (!bValue) return -1;

        // Sort based on direction
        let result = 0;
        if (aValue < bValue) result = -1;
        else if (aValue > bValue) result = 1;

        return overdueInvoicesSortDirection === 'desc' ? -result : result;
      });

    // Group by client
    const grouped = overdueProjects.reduce((acc, project) => {
      const clientName = project.clientName || "Unknown Client";
      if (!acc[clientName]) {
        acc[clientName] = {
          clientName,
          projects: [],
          totalOverdue: 0,
          count45to59: 0,
          count60plus: 0,
        };
      }

      acc[clientName].projects.push(project);
      acc[clientName].totalOverdue += project.modelPrice || project.price || 0;

      if (project.daysOverdue >= 60) {
        acc[clientName].count60plus++;
      } else if (project.daysOverdue >= 45) {
        acc[clientName].count45to59++;
      }

      return acc;
    }, {});

    // Convert to array and sort by total amount overdue
    const groupedArray = Object.values(grouped).sort(
      (a, b) => b.totalOverdue - a.totalOverdue,
    );

    const result = {
      all: overdueProjects,
      grouped: groupedArray,
      total: overdueProjects.length,
      count45to59: overdueProjects.filter(
        (p) => p.daysOverdue >= 45 && p.daysOverdue < 60,
      ).length,
      count60plus: overdueProjects.filter((p) => p.daysOverdue >= 60).length,
      totalAmount: overdueProjects.reduce(
        (sum, p) => sum + (p.modelPrice || p.price || 0),
        0,
      ),
    };

    return result;
  }, [projects, overdueInvoicesSortColumn, overdueInvoicesSortDirection, debouncedOverdueSearch, overdueInvoicesShowType]);

  const handleCardClick = (project, source = null) => {
    setSelectedProject(project);
    setSelectedClient(null);
    setModalType("project");
    setModalSource(source);
    setModalOpen(true);
  };

  const handleClientCardClick = (client) => {
    setSelectedClient(client);
    setSelectedProject(null);
    setModalType("client");
    setModalOpen(true);
  };

  const handleRowClick = (project) => {
    setSelectedProject(project);
    setSelectedClient(null);
    setModalType("project");
    setModalSource(null);
    setModalOpen(true);
  };

  const handleClientRowClick = (client) => {
    setSelectedClient(client);
    setSelectedProject(null);
    setSelectedTakeoff(null);
    setModalType("client");
    setModalOpen(true);
  };

  const handleTakeoffRowClick = (takeoff) => {
    setSelectedTakeoff(takeoff);
    setSelectedProject(null);
    setSelectedClient(null);
    setModalType("takeoff");
    setModalOpen(true);
  };

  const handleTakeoffManagementRowClick = (takeoff) => {
    setSelectedTakeoffManagement(takeoff);
    setIsTakeoffManagementModalOpen(true);
  };

  const handleModalClose = () => {
    setModalOpen(false);
    setSelectedProject(null);
    setSelectedClient(null);
    setSelectedTakeoff(null);
    setModalSource(null);
  };

  const handleModalSave = useCallback(async () => {
    const currentProjectId = selectedProject?.id;
    const currentClientId = selectedClient?.id;
    const currentTakeoffId = selectedTakeoff?.id;

    // Refresh all data from context
    await loadAllData(true);

    // Update selected items with refreshed data
    if (currentProjectId) {
      const updatedProject = projects.find(
        (p) => p.id === currentProjectId,
      );
      if (updatedProject) {
        setSelectedProject(updatedProject);
      }
    }
    if (currentClientId) {
      const updatedClient = clientsWithBilling.find(
        (c) => c.id === currentClientId,
      );
      if (updatedClient) {
        setSelectedClient(updatedClient);
      }
    }
    if (currentTakeoffId) {
      const updatedTakeoff = takeoffs.find(
        (t) => t.id === currentTakeoffId,
      );
      if (updatedTakeoff) {
        setSelectedTakeoff(updatedTakeoff);
      }
    }
  }, [selectedProject, selectedClient, selectedTakeoff, loadAllData, projects, clientsWithBilling, takeoffs]);


  if (loading) {
    return (
      <div style={{ padding: 'var(--space-6)', width: '100%' }}>
        <LoadingSpinner />
      </div>
    );
  }

  if (error) {
    return (
      <div style={{ padding: 'var(--space-6)', width: '100%' }}>
        <ErrorMessage message={error} onRetry={() => loadAllData(true)} />
      </div>
    );
  }

  return (
    <div data-theme={theme} style={{
      padding: 'var(--space-6)',
      width: '100%',
      minHeight: 'calc(100vh - 60px)',
      background: 'var(--dark-bg)',
      color: 'var(--gray-50)'
    }}>
      {/* Header */}
      <div style={{
        marginBottom: 'var(--space-8)',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'space-between'
      }}>
        <h1 style={{ margin: 0, fontSize: 'var(--text-3xl)', fontWeight: 'var(--font-semibold)', color: 'var(--text-primary)' }}>
          Office Admin
        </h1>
        <button
          onClick={toggleTheme}
          className="theme-toggle"
          title={theme === 'dark' ? 'Switch to light mode' : 'Switch to dark mode'}
          style={{
            display: 'flex',
            alignItems: 'center',
            gap: 'var(--space-2)',
          }}
        >
          {theme === 'dark' ? <Sun size={20} /> : <Moon size={20} />}
          <span style={{ fontSize: 'var(--text-sm)' }}>
            {theme === 'dark' ? 'Light' : 'Dark'}
          </span>
        </button>
      </div>

      {/* Tabs */}
      <div style={{
        display: 'flex',
        gap: 'var(--space-2)',
        marginBottom: 'var(--space-5)',
        borderBottom: '2px solid var(--gray-700)',
        paddingBottom: 0,
      }}>
        <button
          onClick={() => setActiveTab("action-items")}
          style={{
            display: 'flex',
            alignItems: 'center',
            gap: 'var(--space-2)',
            padding: 'var(--space-3) var(--space-5)',
            background: activeTab === "action-items" ? 'var(--dark-surface)' : 'transparent',
            border: 'none',
            borderBottom: activeTab === "action-items" ? '3px solid var(--cta-blue)' : '3px solid transparent',
            fontSize: 'var(--text-sm)',
            fontWeight: 'var(--font-medium)',
            cursor: 'pointer',
            transition: 'all var(--transition-fast)',
            position: 'relative',
            marginBottom: '-2px',
            color: activeTab === "action-items" ? 'var(--cta-blue)' : 'var(--gray-400)',
          }}
        >
          <LayoutGrid size={18} />
          Action Items
        </button>
        <button
          onClick={() => setActiveTab("overdue-invoices")}
          style={{
            display: 'flex',
            alignItems: 'center',
            gap: 'var(--space-2)',
            padding: 'var(--space-3) var(--space-5)',
            background: activeTab === "overdue-invoices" ? 'var(--dark-surface)' : 'transparent',
            border: 'none',
            borderBottom: activeTab === "overdue-invoices" ? '3px solid var(--cta-blue)' : '3px solid transparent',
            fontSize: 'var(--text-sm)',
            fontWeight: 'var(--font-medium)',
            cursor: 'pointer',
            transition: 'all var(--transition-fast)',
            position: 'relative',
            marginBottom: '-2px',
            color: activeTab === "overdue-invoices" ? 'var(--cta-blue)' : 'var(--gray-400)',
          }}
        >
          <AlertTriangle size={18} />
          Overdue Invoices
          <span style={{
            background: activeTab === "overdue-invoices" ? 'var(--cta-blue)' : 'var(--gray-600)',
            color: 'white',
            padding: '2px var(--space-2)',
            borderRadius: 'var(--radius-full)',
            fontSize: 'var(--text-xs)',
            fontWeight: 'var(--font-semibold)',
            marginLeft: 'var(--space-1)',
          }}>
            {overdueInvoices.total}
          </span>
        </button>
        <button
          onClick={() => setActiveTab("takeoff-management")}
          style={{
            display: 'flex',
            alignItems: 'center',
            gap: 'var(--space-2)',
            padding: 'var(--space-3) var(--space-5)',
            background: activeTab === "takeoff-management" ? 'var(--dark-surface)' : 'transparent',
            border: 'none',
            borderBottom: activeTab === "takeoff-management" ? '3px solid var(--cta-blue)' : '3px solid transparent',
            fontSize: 'var(--text-sm)',
            fontWeight: 'var(--font-medium)',
            cursor: 'pointer',
            transition: 'all var(--transition-fast)',
            position: 'relative',
            marginBottom: '-2px',
            color: activeTab === "takeoff-management" ? 'var(--cta-blue)' : 'var(--gray-400)',
          }}
        >
          <Layers size={18} />
          Takeoff Invoicing
          <span style={{
            background: activeTab === "takeoff-management" ? 'var(--cta-blue)' : 'var(--gray-600)',
            color: 'white',
            padding: '2px var(--space-2)',
            borderRadius: 'var(--radius-full)',
            fontSize: 'var(--text-xs)',
            fontWeight: 'var(--font-semibold)',
            marginLeft: 'var(--space-1)',
          }}>
            {takeoffs.length}
          </span>
        </button>
        <button
          onClick={() => setActiveTab("all-jobs")}
          style={{
            display: 'flex',
            alignItems: 'center',
            gap: 'var(--space-2)',
            padding: 'var(--space-3) var(--space-5)',
            background: activeTab === "all-jobs" ? 'var(--dark-surface)' : 'transparent',
            border: 'none',
            borderBottom: activeTab === "all-jobs" ? '3px solid var(--cta-blue)' : '3px solid transparent',
            fontSize: 'var(--text-sm)',
            fontWeight: 'var(--font-medium)',
            cursor: 'pointer',
            transition: 'all var(--transition-fast)',
            position: 'relative',
            marginBottom: '-2px',
            color: activeTab === "all-jobs" ? 'var(--cta-blue)' : 'var(--gray-400)',
          }}
        >
          <Briefcase size={18} />
          All Projects
          <span style={{
            background: activeTab === "all-jobs" ? 'var(--cta-blue)' : 'var(--gray-600)',
            color: 'white',
            padding: '2px var(--space-2)',
            borderRadius: 'var(--radius-full)',
            fontSize: 'var(--text-xs)',
            fontWeight: 'var(--font-semibold)',
            marginLeft: 'var(--space-1)',
          }}>
            {projects.length}
          </span>
        </button>
        <button
          onClick={() => setActiveTab("all-clients")}
          style={{
            display: 'flex',
            alignItems: 'center',
            gap: 'var(--space-2)',
            padding: 'var(--space-3) var(--space-5)',
            background: activeTab === "all-clients" ? 'var(--dark-surface)' : 'transparent',
            border: 'none',
            borderBottom: activeTab === "all-clients" ? '3px solid var(--cta-blue)' : '3px solid transparent',
            fontSize: 'var(--text-sm)',
            fontWeight: 'var(--font-medium)',
            cursor: 'pointer',
            transition: 'all var(--transition-fast)',
            position: 'relative',
            marginBottom: '-2px',
            color: activeTab === "all-clients" ? 'var(--cta-blue)' : 'var(--gray-400)',
          }}
        >
          <Users size={18} />
          All Clients
          <span style={{
            background: activeTab === "all-clients" ? 'var(--cta-blue)' : 'var(--gray-600)',
            color: 'white',
            padding: '2px var(--space-2)',
            borderRadius: 'var(--radius-full)',
            fontSize: 'var(--text-xs)',
            fontWeight: 'var(--font-semibold)',
            marginLeft: 'var(--space-1)',
          }}>
            {clientsWithBilling.length}
          </span>
        </button>
      </div>

      {/* Content */}
      <div style={{ minHeight: '400px' }}>
        {activeTab === "action-items" ? (
          <div style={{ display: 'flex', flexDirection: 'column', gap: 'var(--space-6)' }}>
            {/* Ready to Invoice - AB Civil Section */}
            {readyToInvoiceABCivil.length > 0 && (
              <div style={{
                background: 'var(--dark-surface)',
                borderRadius: 'var(--radius-xl)',
                padding: 'var(--space-5)',
                boxShadow: 'var(--shadow-md)',
              }}>
                <div style={{
                  display: 'flex',
                  alignItems: 'center',
                  gap: 'var(--space-3)',
                  marginBottom: 'var(--space-5)',
                  paddingBottom: 'var(--space-4)',
                  borderBottom: '2px solid var(--gray-700)',
                }}>
                  <FileText size={20} style={{ color: 'var(--gray-400)' }} />
                  <h2 style={{ margin: 0, fontSize: 'var(--text-xl)', fontWeight: 'var(--font-semibold)', color: 'var(--gray-50)' }}>
                    Ready to Invoice - AB Civil
                  </h2>
                  <span style={{
                    marginLeft: 'auto',
                    background: 'var(--cta-orange)',
                    color: 'white',
                    padding: 'var(--space-1) var(--space-3)',
                    borderRadius: 'var(--radius-full)',
                    fontSize: 'var(--text-sm)',
                    fontWeight: 'var(--font-medium)',
                  }}>
                    {readyToInvoiceABCivil.length}
                  </span>
                </div>
                <div style={{
                  display: 'grid',
                  gridTemplateColumns: 'repeat(auto-fill, minmax(280px, 1fr))',
                  gap: 'var(--space-4)',
                }}>
                  {(showAllReadyToInvoiceABCivil ? readyToInvoiceABCivil : readyToInvoiceABCivil.slice(0, INITIAL_ITEMS_COUNT)).map((project) => (
                    <OfficeAdminCard
                      key={project.id}
                      project={project}
                      onClick={() => handleCardClick(project)}
                    />
                  ))}
                </div>
                {readyToInvoiceABCivil.length > INITIAL_ITEMS_COUNT && (
                  <div style={{ marginTop: 'var(--space-4)', textAlign: 'center' }}>
                    <Button
                      variant="ghost"
                      size="sm"
                      onClick={() => setShowAllReadyToInvoiceABCivil(!showAllReadyToInvoiceABCivil)}
                    >
                      {showAllReadyToInvoiceABCivil ? 'Show Less' : `Show ${readyToInvoiceABCivil.length - INITIAL_ITEMS_COUNT} More`}
                    </Button>
                  </div>
                )}
              </div>
            )}

            {/* Ready to Invoice - AB Civil NC Section */}
            {readyToInvoiceABCivilNC.length > 0 && (
              <div style={{
                background: 'var(--dark-surface)',
                borderRadius: 'var(--radius-xl)',
                padding: 'var(--space-5)',
                boxShadow: 'var(--shadow-md)',
              }}>
                <div style={{
                  display: 'flex',
                  alignItems: 'center',
                  gap: 'var(--space-3)',
                  marginBottom: 'var(--space-5)',
                  paddingBottom: 'var(--space-4)',
                  borderBottom: '2px solid var(--gray-700)',
                }}>
                  <FileText size={20} style={{ color: 'var(--gray-400)' }} />
                  <h2 style={{ margin: 0, fontSize: 'var(--text-xl)', fontWeight: 'var(--font-semibold)', color: 'var(--gray-50)' }}>
                    Ready to Invoice - AB Civil NC
                  </h2>
                  <span style={{
                    marginLeft: 'auto',
                    background: 'var(--cta-orange)',
                    color: 'white',
                    padding: 'var(--space-1) var(--space-3)',
                    borderRadius: 'var(--radius-full)',
                    fontSize: 'var(--text-sm)',
                    fontWeight: 'var(--font-medium)',
                  }}>
                    {readyToInvoiceABCivilNC.length}
                  </span>
                </div>
                <div style={{
                  display: 'grid',
                  gridTemplateColumns: 'repeat(auto-fill, minmax(280px, 1fr))',
                  gap: 'var(--space-4)',
                }}>
                  {(showAllReadyToInvoiceABCivilNC ? readyToInvoiceABCivilNC : readyToInvoiceABCivilNC.slice(0, INITIAL_ITEMS_COUNT)).map((project) => (
                    <OfficeAdminCard
                      key={project.id}
                      project={project}
                      onClick={() => handleCardClick(project)}
                    />
                  ))}
                </div>
                {readyToInvoiceABCivilNC.length > INITIAL_ITEMS_COUNT && (
                  <div style={{ marginTop: 'var(--space-4)', textAlign: 'center' }}>
                    <Button
                      variant="ghost"
                      size="sm"
                      onClick={() => setShowAllReadyToInvoiceABCivilNC(!showAllReadyToInvoiceABCivilNC)}
                    >
                      {showAllReadyToInvoiceABCivilNC ? 'Show Less' : `Show ${readyToInvoiceABCivilNC.length - INITIAL_ITEMS_COUNT} More`}
                    </Button>
                  </div>
                )}
              </div>
            )}

            {/* Agreement Ready Section */}
            {agreementReady.length > 0 && (
              <div style={{
                background: 'var(--dark-surface)',
                borderRadius: 'var(--radius-xl)',
                padding: 'var(--space-5)',
                boxShadow: 'var(--shadow-md)',
              }}>
                <div style={{
                  display: 'flex',
                  alignItems: 'center',
                  gap: 'var(--space-3)',
                  marginBottom: 'var(--space-5)',
                  paddingBottom: 'var(--space-4)',
                  borderBottom: '2px solid var(--gray-700)',
                }}>
                  <FileText size={20} style={{ color: 'var(--gray-400)' }} />
                  <h2 style={{ margin: 0, fontSize: 'var(--text-xl)', fontWeight: 'var(--font-semibold)', color: 'var(--gray-50)' }}>
                    Agreement Ready
                  </h2>
                  <span style={{
                    marginLeft: 'auto',
                    background: 'var(--cta-orange)',
                    color: 'white',
                    padding: 'var(--space-1) var(--space-3)',
                    borderRadius: 'var(--radius-full)',
                    fontSize: 'var(--text-sm)',
                    fontWeight: 'var(--font-medium)',
                  }}>
                    {agreementReady.length}
                  </span>
                </div>
                <div style={{
                  display: 'grid',
                  gridTemplateColumns: 'repeat(auto-fill, minmax(280px, 1fr))',
                  gap: 'var(--space-4)',
                }}>
                  {(showAllAgreementReady ? agreementReady : agreementReady.slice(0, INITIAL_ITEMS_COUNT)).map((project) => (
                    <OfficeAdminCard
                      key={project.id}
                      project={project}
                      showAddress={true}
                      onClick={() => handleCardClick(project, "agreementReady")}
                    />
                  ))}
                </div>
                {agreementReady.length > INITIAL_ITEMS_COUNT && (
                  <div style={{ marginTop: 'var(--space-4)', textAlign: 'center' }}>
                    <Button
                      variant="ghost"
                      size="sm"
                      onClick={() => setShowAllAgreementReady(!showAllAgreementReady)}
                    >
                      {showAllAgreementReady ? 'Show Less' : `Show ${agreementReady.length - INITIAL_ITEMS_COUNT} More`}
                    </Button>
                  </div>
                )}
              </div>
            )}

            {/* Ready to Invoice - Remote Services Section */}
            {readyToInvoiceRemoteServices.length > 0 && (
              <div style={{
                background: 'var(--dark-surface)',
                borderRadius: 'var(--radius-xl)',
                padding: 'var(--space-5)',
                boxShadow: 'var(--shadow-md)',
              }}>
                <div style={{
                  display: 'flex',
                  alignItems: 'center',
                  gap: 'var(--space-3)',
                  marginBottom: 'var(--space-5)',
                  paddingBottom: 'var(--space-4)',
                  borderBottom: '2px solid var(--gray-700)',
                }}>
                  <FileText size={20} style={{ color: 'var(--gray-400)' }} />
                  <h2 style={{ margin: 0, fontSize: 'var(--text-xl)', fontWeight: 'var(--font-semibold)', color: 'var(--gray-50)' }}>
                    Ready to Invoice - Remote Services
                  </h2>
                  <span style={{
                    marginLeft: 'auto',
                    background: 'var(--cta-orange)',
                    color: 'white',
                    padding: 'var(--space-1) var(--space-3)',
                    borderRadius: 'var(--radius-full)',
                    fontSize: 'var(--text-sm)',
                    fontWeight: 'var(--font-medium)',
                  }}>
                    {readyToInvoiceRemoteServices.length}
                  </span>
                </div>
                <div style={{
                  display: 'grid',
                  gridTemplateColumns: 'repeat(auto-fill, minmax(280px, 1fr))',
                  gap: 'var(--space-4)',
                }}>
                  {(showAllReadyToInvoiceRemoteServices ? readyToInvoiceRemoteServices : readyToInvoiceRemoteServices.slice(0, INITIAL_ITEMS_COUNT)).map((client) => (
                    <OfficeAdminCard
                      key={client.id}
                      client={client}
                      isClient={true}
                      onClick={() => handleClientCardClick(client)}
                    />
                  ))}
                </div>
                {readyToInvoiceRemoteServices.length > INITIAL_ITEMS_COUNT && (
                  <div style={{ marginTop: 'var(--space-4)', textAlign: 'center' }}>
                    <Button
                      variant="ghost"
                      size="sm"
                      onClick={() => setShowAllReadyToInvoiceRemoteServices(!showAllReadyToInvoiceRemoteServices)}
                    >
                      {showAllReadyToInvoiceRemoteServices ? 'Show Less' : `Show ${readyToInvoiceRemoteServices.length - INITIAL_ITEMS_COUNT} More`}
                    </Button>
                  </div>
                )}
              </div>
            )}

            {/* Ready to Invoice - Takeoffs Section */}
            {readyToInvoiceTakeoffs.length > 0 && (
              <div style={{
                background: 'var(--dark-surface)',
                borderRadius: 'var(--radius-xl)',
                padding: 'var(--space-5)',
                boxShadow: 'var(--shadow-md)',
              }}>
                <div style={{
                  display: 'flex',
                  alignItems: 'center',
                  gap: 'var(--space-3)',
                  marginBottom: 'var(--space-5)',
                  paddingBottom: 'var(--space-4)',
                  borderBottom: '2px solid var(--gray-700)',
                }}>
                  <FileText size={20} style={{ color: 'var(--gray-400)' }} />
                  <h2 style={{ margin: 0, fontSize: 'var(--text-xl)', fontWeight: 'var(--font-semibold)', color: 'var(--gray-50)' }}>
                    Ready to Invoice - Takeoffs
                  </h2>
                  <span style={{
                    marginLeft: 'auto',
                    background: 'var(--cta-orange)',
                    color: 'white',
                    padding: 'var(--space-1) var(--space-3)',
                    borderRadius: 'var(--radius-full)',
                    fontSize: 'var(--text-sm)',
                    fontWeight: 'var(--font-medium)',
                  }}>
                    {readyToInvoiceTakeoffs.length}
                  </span>
                </div>
                <div style={{
                  display: 'grid',
                  gridTemplateColumns: 'repeat(auto-fill, minmax(280px, 1fr))',
                  gap: 'var(--space-4)',
                }}>
                  {(showAllReadyToInvoiceTakeoffs ? readyToInvoiceTakeoffs : readyToInvoiceTakeoffs.slice(0, INITIAL_ITEMS_COUNT)).map((takeoff) => (
                    <TakeoffCard
                      key={takeoff.id}
                      takeoff={takeoff}
                      onClick={() => handleTakeoffRowClick(takeoff)}
                    />
                  ))}
                </div>
                {readyToInvoiceTakeoffs.length > INITIAL_ITEMS_COUNT && (
                  <div style={{ marginTop: 'var(--space-4)', textAlign: 'center' }}>
                    <Button
                      variant="ghost"
                      size="sm"
                      onClick={() => setShowAllReadyToInvoiceTakeoffs(!showAllReadyToInvoiceTakeoffs)}
                    >
                      {showAllReadyToInvoiceTakeoffs ? 'Show Less' : `Show ${readyToInvoiceTakeoffs.length - INITIAL_ITEMS_COUNT} More`}
                    </Button>
                  </div>
                )}
              </div>
            )}

            {/* Completed - Not Invoiced Section */}
            {completedNotInvoiced.length > 0 && (
              <div style={{
                background: 'var(--dark-surface)',
                borderRadius: 'var(--radius-xl)',
                padding: 'var(--space-5)',
                boxShadow: 'var(--shadow-md)',
              }}>
                <div style={{
                  display: 'flex',
                  alignItems: 'center',
                  gap: 'var(--space-3)',
                  marginBottom: 'var(--space-5)',
                  paddingBottom: 'var(--space-4)',
                  borderBottom: '2px solid var(--gray-700)',
                }}>
                  <FileText size={20} style={{ color: 'var(--gray-400)' }} />
                  <h2 style={{ margin: 0, fontSize: 'var(--text-xl)', fontWeight: 'var(--font-semibold)', color: 'var(--gray-50)' }}>
                    Completed - Not Ready to Invoice
                  </h2>
                  <span style={{
                    marginLeft: 'auto',
                    background: 'var(--cta-orange)',
                    color: 'white',
                    padding: 'var(--space-1) var(--space-3)',
                    borderRadius: 'var(--radius-full)',
                    fontSize: 'var(--text-sm)',
                    fontWeight: 'var(--font-medium)',
                  }}>
                    {completedNotInvoiced.length}
                  </span>
                </div>
                <div style={{
                  display: 'grid',
                  gridTemplateColumns: 'repeat(auto-fill, minmax(280px, 1fr))',
                  gap: 'var(--space-4)',
                }}>
                  {(showAllCompletedNotInvoiced ? completedNotInvoiced : completedNotInvoiced.slice(0, INITIAL_ITEMS_COUNT)).map((project) => (
                    <OfficeAdminCard
                      key={project.id}
                      project={project}
                      onClick={() => handleCardClick(project)}
                    />
                  ))}
                </div>
                {completedNotInvoiced.length > INITIAL_ITEMS_COUNT && (
                  <div style={{ marginTop: 'var(--space-4)', textAlign: 'center' }}>
                    <Button
                      variant="ghost"
                      size="sm"
                      onClick={() => setShowAllCompletedNotInvoiced(!showAllCompletedNotInvoiced)}
                    >
                      {showAllCompletedNotInvoiced ? 'Show Less' : `Show ${completedNotInvoiced.length - INITIAL_ITEMS_COUNT} More`}
                    </Button>
                  </div>
                )}
              </div>
            )}

            {/* Additional sections can be added here following the same pattern */}
          </div>
        ) : activeTab === "overdue-invoices" ? (
          <div style={{
            background: 'var(--dark-surface)',
            borderRadius: 'var(--radius-xl)',
            padding: 'var(--space-5)',
            boxShadow: 'var(--shadow-md)',
          }}>
            <div style={{
              display: 'flex',
              justifyContent: 'space-between',
              alignItems: 'center',
              gap: 'var(--space-4)',
              marginBottom: 'var(--space-5)',
              padding: 'var(--space-4)',
              background: 'var(--dark-bg)',
              borderRadius: 'var(--radius-lg)',
              border: '1px solid var(--gray-700)',
            }}>
              <div className="filters-left">
                <div
                  style={{ display: "flex", alignItems: "center", gap: "20px", flexWrap: "wrap" }}
                >
                  <div
                    style={{
                      display: "flex",
                      alignItems: "center",
                      gap: "12px",
                    }}
                  >
                    <AlertTriangle size={20} style={{ color: "#ef4444" }} />
                    <h2
                      style={{ margin: 0, fontSize: "20px", fontWeight: "600" }}
                    >
                      Overdue Invoices
                    </h2>
                  </div>
                  <div
                    style={{
                      display: "flex",
                      gap: "12px",
                      alignItems: "center",
                    }}
                  >
                    <span
                      className="section-count"
                      style={{ background: "#fef3c7", color: "#92400e" }}
                    >
                      45-59 days: {overdueInvoices.count45to59}
                    </span>
                    <span
                      className="section-count"
                      style={{ background: "#fee2e2", color: "#991b1b" }}
                    >
                      60+ days: {overdueInvoices.count60plus}
                    </span>
                    <span className="section-count" style={{ background: "#e5e7eb", color: "#111827" }}>
                      Total: {overdueInvoices.total}
                    </span>
                  </div>
                </div>
                <div
                  style={{
                    display: "flex",
                    gap: "12px",
                    marginTop: "12px",
                    alignItems: "center",
                    flexWrap: "wrap",
                  }}
                >
                  <div className="search-input-wrapper" style={{ minWidth: "300px" }}>
                    <Search size={18} className="search-icon" />
                    <input
                      type="text"
                      className="search-input"
                      placeholder="Search by job number, project name, client, or PO number..."
                      value={overdueSearchQuery}
                      onChange={(e) => updateOverdueSearch(e.target.value)}
                    />
                    {overdueSearchQuery && (
                      <button
                        className="search-clear-btn"
                        onClick={() => clearOverdueSearch()}
                        title="Clear search"
                      >
                        <X size={16} />
                      </button>
                    )}
                  </div>

                  {/* Project/Takeoff Toggle */}
                  <div className="toggle-button-group" style={{
                    display: 'flex',
                    gap: '4px',
                    padding: '4px',
                    background: 'var(--background-secondary, #f3f4f6)',
                    borderRadius: '8px',
                    border: '1px solid var(--border-primary, #e5e7eb)'
                  }}>
                    <button
                      className={`toggle-button ${overdueInvoicesShowType === 'all' ? 'active' : ''}`}
                      onClick={() => setOverdueInvoicesShowType('all')}
                      style={{
                        padding: '6px 12px',
                        background: overdueInvoicesShowType === 'all' ? 'var(--background-primary, #ffffff)' : 'transparent',
                        border: 'none',
                        borderRadius: '4px',
                        cursor: 'pointer',
                        fontSize: '14px',
                        fontWeight: overdueInvoicesShowType === 'all' ? '500' : '400',
                        color: overdueInvoicesShowType === 'all' ? 'var(--text-primary, #111827)' : 'var(--text-secondary, #6b7280)',
                        transition: 'all 0.2s',
                        boxShadow: overdueInvoicesShowType === 'all' ? '0 1px 2px rgba(0, 0, 0, 0.05)' : 'none'
                      }}
                    >
                      All
                    </button>
                    <button
                      className={`toggle-button ${overdueInvoicesShowType === 'projects' ? 'active' : ''}`}
                      onClick={() => setOverdueInvoicesShowType('projects')}
                      style={{
                        padding: '6px 12px',
                        background: overdueInvoicesShowType === 'projects' ? 'var(--background-primary, #ffffff)' : 'transparent',
                        border: 'none',
                        borderRadius: '4px',
                        cursor: 'pointer',
                        fontSize: '14px',
                        fontWeight: overdueInvoicesShowType === 'projects' ? '500' : '400',
                        color: overdueInvoicesShowType === 'projects' ? 'var(--text-primary, #111827)' : 'var(--text-secondary, #6b7280)',
                        transition: 'all 0.2s',
                        boxShadow: overdueInvoicesShowType === 'projects' ? '0 1px 2px rgba(0, 0, 0, 0.05)' : 'none'
                      }}
                    >
                      Projects
                    </button>
                    <button
                      className={`toggle-button ${overdueInvoicesShowType === 'takeoffs' ? 'active' : ''}`}
                      onClick={() => setOverdueInvoicesShowType('takeoffs')}
                      style={{
                        padding: '6px 12px',
                        background: overdueInvoicesShowType === 'takeoffs' ? 'var(--background-primary, #ffffff)' : 'transparent',
                        border: 'none',
                        borderRadius: '4px',
                        cursor: 'pointer',
                        fontSize: '14px',
                        fontWeight: overdueInvoicesShowType === 'takeoffs' ? '500' : '400',
                        color: overdueInvoicesShowType === 'takeoffs' ? 'var(--text-primary, #111827)' : 'var(--text-secondary, #6b7280)',
                        transition: 'all 0.2s',
                        boxShadow: overdueInvoicesShowType === 'takeoffs' ? '0 1px 2px rgba(0, 0, 0, 0.05)' : 'none'
                      }}
                    >
                      Takeoffs
                    </button>
                  </div>
                </div>
              </div>
            </div>
            <div className="table-scroll-container">
              <table className="office-admin-table">
                <thead>
                  <tr>
                    <th style={{ width: '50px', textAlign: 'center' }}></th>
                    <th
                      className="sortable"
                      onClick={() => handleOverdueInvoicesSort('jobNumber')}
                    >
                      <div className="th-content">
                        <span>Job Number</span>
                        {overdueInvoicesSortColumn === 'jobNumber' && (
                          <span className="sort-icon">
                            {overdueInvoicesSortDirection === 'asc' ? (
                              <ChevronUp size={14} />
                            ) : (
                              <ChevronDown size={14} />
                            )}
                          </span>
                        )}
                      </div>
                    </th>
                    <th>Project Name</th>
                    <th
                      className="sortable"
                      onClick={() => handleOverdueInvoicesSort('clientName')}
                    >
                      <div className="th-content">
                        <span>Client</span>
                        {overdueInvoicesSortColumn === 'clientName' && (
                          <span className="sort-icon">
                            {overdueInvoicesSortDirection === 'asc' ? (
                              <ChevronUp size={14} />
                            ) : (
                              <ChevronDown size={14} />
                            )}
                          </span>
                        )}
                      </div>
                    </th>
                    <th
                      className="sortable"
                      onClick={() => handleOverdueInvoicesSort('daysOverdue')}
                    >
                      <div className="th-content">
                        <span>Days Overdue</span>
                        {overdueInvoicesSortColumn === 'daysOverdue' && (
                          <span className="sort-icon">
                            {overdueInvoicesSortDirection === 'asc' ? (
                              <ChevronUp size={14} />
                            ) : (
                              <ChevronDown size={14} />
                            )}
                          </span>
                        )}
                      </div>
                    </th>
                    <th>Invoice Date</th>
                    <th>Amount</th>
                    <th>PO Number</th>
                  </tr>
                </thead>
                <tbody>
                  {overdueInvoices.all.length === 0 ? (
                    <tr>
                      <td colSpan={8} className="empty-row">
                        No overdue invoices found
                      </td>
                    </tr>
                  ) : (
                    overdueInvoices.all.map((project) => (
                      <tr
                        key={project.id}
                        onClick={() => handleRowClick(project)}
                      >
                        <td style={{ textAlign: 'center', padding: '12px 16px' }}>
                          <button
                            onClick={(e) => {
                              e.stopPropagation();
                              handleRowClick(project);
                            }}
                            style={{
                              padding: '8px',
                              background: 'none',
                              border: 'none',
                              color: 'var(--gray-400)',
                              cursor: 'pointer',
                              borderRadius: '8px',
                              transition: 'background-color 0.2s, color 0.2s, transform 0.2s',
                              display: 'flex',
                              alignItems: 'center',
                              justifyContent: 'center',
                              width: '36px',
                              height: '36px',
                            }}
                            onMouseEnter={(e) => {
                              e.currentTarget.style.backgroundColor = 'var(--gray-800)';
                              e.currentTarget.style.color = 'var(--cta-blue)';
                              e.currentTarget.style.transform = 'scale(1.05)';
                            }}
                            onMouseLeave={(e) => {
                              e.currentTarget.style.backgroundColor = 'transparent';
                              e.currentTarget.style.color = 'var(--gray-400)';
                              e.currentTarget.style.transform = 'scale(1)';
                            }}
                            title="View Details"
                          >
                            <Info size={20} />
                          </button>
                        </td>
                        <td className="job-number-cell">
                          {`${project.jobNumber || "-"}${project.rev ? ` Rev ${project.rev}` : ""}`}
                        </td>
                        <td className="project-name">
                          {project.projectName || "-"}
                        </td>
                        <td>{project.clientName || "-"}</td>
                        <td>
                          <span
                            className={`status-pill ${
                              project.daysOverdue >= 60
                                ? "overdue-critical"
                                : "overdue-warning"
                            }`}
                          >
                            {project.daysOverdue} days
                          </span>
                        </td>
                        <td>
                          {project.invoiceDate || project.invoicedDate
                            ? new Date(
                                project.invoiceDate || project.invoicedDate,
                              ).toLocaleDateString()
                            : "-"}
                        </td>
                        <td className="price-cell">
                          {project.modelPrice || project.price
                            ? `$${(project.modelPrice || project.price).toLocaleString()}`
                            : "TBD"}
                        </td>
                        <td>{project.poNumber || "-"}</td>
                      </tr>
                    ))
                  )}
                </tbody>
              </table>
            </div>
            {/* Search Results Info */}
            {debouncedOverdueSearch && (
              <div className="search-results-info" style={{ marginTop: "8px", color: "var(--text-secondary)" }}>
                {isOverdueSearching ? (
                  <span className="searching-indicator">Searching...</span>
                ) : (
                  <span className="search-results-count">
                    Found {overdueInvoices.all.length} overdue invoice{overdueInvoices.all.length !== 1 ? 's' : ''}
                    {debouncedOverdueSearch && ` matching "${debouncedOverdueSearch}"`}
                  </span>
                )}
              </div>
            )}
          </div>
        ) : activeTab === "all-clients" ? (
          <div style={{
            background: 'var(--dark-surface)',
            borderRadius: 'var(--radius-xl)',
            overflow: 'hidden',
            border: '1px solid var(--gray-600)',
            display: 'flex',
            flexDirection: 'column',
            maxHeight: '85vh',
          }}>
            {/* Toolbar */}
            <div style={{
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'space-between',
              padding: 'var(--space-4)',
              backgroundColor: 'var(--dark-bg)',
              borderBottom: '1px solid var(--gray-600)',
              gap: 'var(--space-4)',
              flexShrink: 0,
            }}>
              <div style={{ width: '300px', flexShrink: 0 }}>
                <SearchBar
                  value={clientSearchQuery}
                  onChange={(e) => {
                    updateClientSearch(e.target.value);
                    setClientsCurrentPage(1);
                  }}
                  placeholder="Search all clients..."
                  size="sm"
                />
              </div>
              <div style={{ display: 'flex', gap: 'var(--space-3)', alignItems: 'center' }}>
                <Popover
                  isOpen={showClientsFilters}
                  onClose={() => setShowClientsFilters(false)}
                  trigger={
                    <Button
                      variant="ghost"
                      size="sm"
                      onClick={() => {
                        setShowClientsFilters(!showClientsFilters);
                        setShowClientsColumns(false);
                      }}
                    >
                      <Filter size={16} />
                      Filters
                    </Button>
                  }
                  position="bottom-right"
                  width="600px"
                  maxWidth="90vw"
                >
                  <div style={{ display: 'flex', flexDirection: 'column', gap: 'var(--space-4)' }}>
                    <h3 style={{
                      margin: 0,
                      fontSize: 'var(--text-base)',
                      fontWeight: 'var(--font-semibold)',
                      color: 'var(--gray-200)'
                    }}>
                      Filter Clients
                    </h3>

                    {/* Billing Status Filter */}
                    <MultiSelectDropdown
                      label="Billing Status"
                      options={clientBillingStatusOptions}
                      selected={clientBillingStatusFilter}
                      onChange={setClientBillingStatusFilter}
                      placeholder="All Statuses"
                    />

                    {/* Client Status Filter */}
                    <MultiSelectDropdown
                      label="Client Status"
                      options={clientStatusOptions}
                      selected={clientStatusFilter}
                      onChange={setClientStatusFilter}
                      placeholder="All Statuses"
                    />

                    {/* Client Of Filter */}
                    <MultiSelectDropdown
                      label="Client Of"
                      options={clientOfOptions}
                      selected={clientOfFilter}
                      onChange={setClientOfFilter}
                      placeholder="All"
                    />

                    {/* Advanced Filters */}
                    <div>
                      <h4 style={{
                        margin: 0,
                        marginBottom: 'var(--space-3)',
                        fontSize: 'var(--text-sm)',
                        fontWeight: 'var(--font-semibold)',
                        color: 'var(--gray-300)',
                        textTransform: 'uppercase',
                        letterSpacing: '0.5px'
                      }}>
                        Advanced Filters
                      </h4>
                      <CustomFieldFilter
                        currentFilters={customFilters}
                        onFiltersChange={setCustomFilters}
                      />
                    </div>

                    {/* Footer Actions */}
                    <div style={{
                      display: 'flex',
                      justifyContent: 'space-between',
                      paddingTop: 'var(--space-3)',
                      borderTop: '1px solid var(--gray-700)'
                    }}>
                      <Button
                        variant="ghost"
                        size="sm"
                        onClick={() => {
                          clearClientSearch();
                          setClientBillingStatusFilter([]);
                          setClientStatusFilter([]);
                          setClientOfFilter([]);
                          setCustomFilters([]);
                          setClientsCurrentPage(1);
                        }}
                      >
                        Clear all
                      </Button>
                      <Button
                        variant="primary"
                        size="sm"
                        onClick={() => setShowClientsFilters(false)}
                      >
                        Apply
                      </Button>
                    </div>
                  </div>
                </Popover>

                <Popover
                  isOpen={showClientsColumns}
                  onClose={() => setShowClientsColumns(false)}
                  trigger={
                    <Button
                      variant="ghost"
                      size="sm"
                      onClick={() => {
                        setShowClientsColumns(!showClientsColumns);
                        setShowClientsFilters(false);
                      }}
                    >
                      <Settings size={16} />
                      Columns
                    </Button>
                  }
                  position="bottom-right"
                  width="300px"
                  maxWidth="90vw"
                >
                  <div style={{ display: 'flex', flexDirection: 'column', gap: 'var(--space-4)' }}>
                    <h3 style={{
                      margin: 0,
                      fontSize: 'var(--text-base)',
                      fontWeight: 'var(--font-semibold)',
                      color: 'var(--gray-200)'
                    }}>
                      Toggle Columns
                    </h3>

                    {/* Column checkboxes */}
                    <div style={{ display: 'flex', flexDirection: 'column', gap: 'var(--space-2)' }}>
                      {[
                        { key: "action", label: "View Icon" },
                        { key: "name", label: "Client Name" },
                        { key: "billingStatus", label: "Billing Status" },
                        { key: "yearlyCost", label: "Yearly Cost" },
                        { key: "clientsInvoicedDate", label: "Invoiced Date" },
                        { key: "clientOf", label: "Client Of" },
                        { key: "clientStatus", label: "Client Status" },
                        { key: "state", label: "State" },
                        { key: "platform", label: "Platform" },
                        { key: "address", label: "Address" },
                        { key: "phoneNumber", label: "Phone Number" },
                      ].map((col) => (
                        <label
                          key={col.key}
                          style={{
                            display: 'flex',
                            alignItems: 'center',
                            gap: 'var(--space-2)',
                            padding: 'var(--space-2)',
                            cursor: 'pointer',
                            borderRadius: 'var(--radius-md)',
                            transition: 'background-color var(--transition-fast)',
                          }}
                          onMouseEnter={(e) => e.currentTarget.style.backgroundColor = 'var(--dark-elevated)'}
                          onMouseLeave={(e) => e.currentTarget.style.backgroundColor = 'transparent'}
                        >
                          <input
                            type="checkbox"
                            checked={clientVisibleColumns[col.key]}
                            onChange={() => handleClientColumnToggle(col.key)}
                            style={{ cursor: 'pointer' }}
                          />
                          <span style={{ fontSize: 'var(--text-sm)', color: 'var(--gray-200)' }}>
                            {col.label}
                          </span>
                        </label>
                      ))}
                    </div>
                  </div>
                </Popover>

                <Button
                  variant={clientsAutoResize ? 'secondary' : 'ghost'}
                  size="sm"
                  onClick={() => setClientsAutoResize(!clientsAutoResize)}
                >
                  {clientsAutoResize ? '✓ ' : ''}Auto-resize
                </Button>
              </div>
            </div>
            {/* Table Container */}
            <div style={{
              backgroundColor: 'var(--dark-surface)',
              overflowX: 'auto',
              overflowY: 'auto',
              flex: 1,
              minWidth: 0,
            }}>
              <AllClientsTable
                clients={paginatedClients}
                onRowClick={handleClientRowClick}
                onClientUpdate={() => loadAllData(true)}
                visibleColumns={clientVisibleColumns}
                autoResizeEnabled={clientsAutoResize}
              />
            </div>

            {/* Pagination and Results Info */}
            <div style={{
              display: 'flex',
              flexDirection: 'column',
              gap: 'var(--space-3)',
              padding: 'var(--space-4)',
              backgroundColor: 'var(--dark-bg)',
              borderTop: '1px solid var(--gray-600)',
              flexShrink: 0,
            }}>
              {debouncedClientSearch && (
                <div style={{
                  fontSize: 'var(--text-sm)',
                  color: 'var(--gray-400)',
                }}>
                  {isClientSearching ? (
                    <span>Searching...</span>
                  ) : (
                    <span>
                      Found {filteredClients.length} client{filteredClients.length !== 1 ? 's' : ''}
                      {debouncedClientSearch && ` matching "${debouncedClientSearch}"`}
                    </span>
                  )}
                </div>
              )}
              {filteredClients.length > 0 && (
                <div style={{
                  display: 'flex',
                  justifyContent: 'space-between',
                  alignItems: 'center',
                  flexWrap: 'wrap',
                  gap: 'var(--space-3)',
                }}>
                  <div style={{ fontSize: 'var(--text-sm)', color: 'var(--gray-400)' }}>
                    Showing {clientsShowingStart}-{clientsShowingEnd} of {filteredClients.length} clients
                  </div>
                  {clientsTotalPages > 1 && (
                    <div style={{ display: 'flex', gap: 'var(--space-2)' }}>
                      <Button
                        variant="ghost"
                        size="sm"
                        onClick={() => setClientsCurrentPage(clientsCurrentPage - 1)}
                        disabled={clientsCurrentPage === 1}
                      >
                        Previous
                      </Button>

                      {/* Page numbers */}
                      {Array.from({ length: Math.min(clientsTotalPages, 5) }, (_, i) => {
                        let pageNum;
                        if (clientsTotalPages <= 5) {
                          pageNum = i + 1;
                        } else if (clientsCurrentPage <= 3) {
                          pageNum = i + 1;
                        } else if (clientsCurrentPage >= clientsTotalPages - 2) {
                          pageNum = clientsTotalPages - 4 + i;
                        } else {
                          pageNum = clientsCurrentPage - 2 + i;
                        }

                        return (
                          <Button
                            key={pageNum}
                            variant={clientsCurrentPage === pageNum ? 'secondary' : 'ghost'}
                            size="sm"
                            onClick={() => setClientsCurrentPage(pageNum)}
                          >
                            {pageNum}
                          </Button>
                        );
                      })}

                      <Button
                        variant="ghost"
                        size="sm"
                        onClick={() => setClientsCurrentPage(clientsCurrentPage + 1)}
                        disabled={clientsCurrentPage === clientsTotalPages}
                      >
                        Next
                      </Button>
                    </div>
                  )}
                </div>
              )}
            </div>
          </div>
        ) : activeTab === "takeoff-management" ? (
          <div style={{
            background: 'var(--dark-surface)',
            borderRadius: 'var(--radius-xl)',
            overflow: 'hidden',
            border: '1px solid var(--gray-600)',
            display: 'flex',
            flexDirection: 'column',
            maxHeight: '85vh',
          }}>
            {/* Toolbar */}
            <div style={{
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'space-between',
              padding: 'var(--space-4)',
              backgroundColor: 'var(--dark-bg)',
              borderBottom: '1px solid var(--gray-600)',
              gap: 'var(--space-4)',
              flexShrink: 0,
            }}>
              <div style={{ width: '300px', flexShrink: 0 }}>
                <SearchBar
                  value={takeoffSearchQuery}
                  onChange={(e) => {
                    updateTakeoffSearch(e.target.value);
                    setTakeoffCurrentPage(1);
                  }}
                  placeholder="Search takeoffs..."
                  size="sm"
                />
              </div>
              <div style={{ display: 'flex', gap: 'var(--space-3)', alignItems: 'center' }}>
                <Popover
                  isOpen={showTakeoffFilters}
                  onClose={() => setShowTakeoffFilters(false)}
                  trigger={
                    <Button
                      variant="ghost"
                      size="sm"
                      onClick={() => {
                        setShowTakeoffFilters(!showTakeoffFilters);
                        setShowTakeoffColumns(false);
                      }}
                    >
                      <Filter size={16} />
                      Filters
                    </Button>
                  }
                  position="bottom-right"
                  width="400px"
                  maxWidth="90vw"
                >
                  <div style={{ display: 'flex', flexDirection: 'column', gap: 'var(--space-4)' }}>
                    <h3 style={{
                      margin: 0,
                      fontSize: 'var(--text-base)',
                      fontWeight: 'var(--font-semibold)',
                      color: 'var(--gray-200)'
                    }}>
                      Filter Takeoffs
                    </h3>

                    {/* Status Filter */}
                    <MultiSelectDropdown
                      label="Status"
                      options={takeoffStatusOptions}
                      selected={takeoffStatusFilter}
                      onChange={setTakeoffStatusFilter}
                      placeholder="All Statuses"
                    />

                    {/* Invoiced Status Filter */}
                    <MultiSelectDropdown
                      label="Invoiced Status"
                      options={takeoffInvoicedStatusOptions}
                      selected={takeoffInvoicedStatusFilter}
                      onChange={setTakeoffInvoicedStatusFilter}
                      placeholder="All Statuses"
                    />

                    {/* Footer Actions */}
                    <div style={{
                      display: 'flex',
                      justifyContent: 'space-between',
                      paddingTop: 'var(--space-3)',
                      borderTop: '1px solid var(--gray-700)'
                    }}>
                      <Button
                        variant="ghost"
                        size="sm"
                        onClick={() => {
                          clearTakeoffSearch();
                          setTakeoffStatusFilter([]);
                          setTakeoffInvoicedStatusFilter([]);
                          setTakeoffCurrentPage(1);
                        }}
                      >
                        Clear all
                      </Button>
                      <Button
                        variant="primary"
                        size="sm"
                        onClick={() => setShowTakeoffFilters(false)}
                      >
                        Apply
                      </Button>
                    </div>
                  </div>
                </Popover>

                <Popover
                  isOpen={showTakeoffColumns}
                  onClose={() => setShowTakeoffColumns(false)}
                  trigger={
                    <Button
                      variant="ghost"
                      size="sm"
                      onClick={() => {
                        setShowTakeoffColumns(!showTakeoffColumns);
                        setShowTakeoffFilters(false);
                      }}
                    >
                      <Settings size={16} />
                      Columns
                    </Button>
                  }
                  position="bottom-right"
                  width="300px"
                  maxWidth="90vw"
                >
                  <div style={{ display: 'flex', flexDirection: 'column', gap: 'var(--space-4)' }}>
                    <h3 style={{
                      margin: 0,
                      fontSize: 'var(--text-base)',
                      fontWeight: 'var(--font-semibold)',
                      color: 'var(--gray-200)'
                    }}>
                      Toggle Columns
                    </h3>

                    {/* Column checkboxes */}
                    <div style={{ display: 'flex', flexDirection: 'column', gap: 'var(--space-2)' }}>
                      {[
                        { key: "action", label: "View Icon" },
                        { key: "projectName", label: "Projects" },
                        { key: "jobNumber", label: "Job Number" },
                        { key: "status", label: "Status" },
                        { key: "abCivilPrice", label: "AB Civil Price" },
                        { key: "ufredoPrice", label: "Ufredo Price" },
                        { key: "formClient", label: "Client" },
                        { key: "invoicedStatus", label: "Invoiced Status" },
                        { key: "invoicedDate", label: "Invoiced Date" },
                        { key: "paidDate", label: "Paid Date" },
                        { key: "earthworksTechInvNumber", label: "Earthworks Tech Inv Number" },
                        { key: "paidToUfredo", label: "Paid to Ufredo" },
                        { key: "paidToUfredoDate", label: "Paid to Ufredo Date" },
                      ].map((col) => (
                        <label
                          key={col.key}
                          style={{
                            display: 'flex',
                            alignItems: 'center',
                            gap: 'var(--space-2)',
                            padding: 'var(--space-2)',
                            cursor: 'pointer',
                            borderRadius: 'var(--radius-md)',
                            transition: 'background-color var(--transition-fast)',
                          }}
                          onMouseEnter={(e) => e.currentTarget.style.backgroundColor = 'var(--dark-elevated)'}
                          onMouseLeave={(e) => e.currentTarget.style.backgroundColor = 'transparent'}
                        >
                          <input
                            type="checkbox"
                            checked={takeoffVisibleColumns[col.key]}
                            onChange={() => handleTakeoffColumnToggle(col.key)}
                            style={{ cursor: 'pointer' }}
                          />
                          <span style={{ fontSize: 'var(--text-sm)', color: 'var(--gray-200)' }}>
                            {col.label}
                          </span>
                        </label>
                      ))}
                    </div>
                  </div>
                </Popover>

                <Button
                  variant={takeoffAutoResize ? 'secondary' : 'ghost'}
                  size="sm"
                  onClick={() => setTakeoffAutoResize(!takeoffAutoResize)}
                >
                  {takeoffAutoResize ? '✓ ' : ''}Auto-resize
                </Button>
              </div>
            </div>
            {/* Table Container */}
            <div style={{
              backgroundColor: 'var(--dark-surface)',
              overflowX: 'auto',
              overflowY: 'auto',
              flex: 1,
              minWidth: 0,
            }}>
              <TakeoffManagementTable
                takeoffs={paginatedTakeoffs}
                clients={clients}
                onRowClick={handleTakeoffManagementRowClick}
                showColumnMenu={showTakeoffManagementColumnMenu}
                setShowColumnMenu={setShowTakeoffManagementColumnMenu}
                onTakeoffUpdate={() => loadAllData(true)}
              />
            </div>

            {/* Pagination and Results Info */}
            <div style={{
              display: 'flex',
              flexDirection: 'column',
              gap: 'var(--space-3)',
              padding: 'var(--space-4)',
              backgroundColor: 'var(--dark-bg)',
              borderTop: '1px solid var(--gray-600)',
              flexShrink: 0,
            }}>
              {debouncedTakeoffSearch && (
                <div style={{
                  fontSize: 'var(--text-sm)',
                  color: 'var(--gray-400)',
                }}>
                  {isTakeoffSearching ? (
                    <span>Searching...</span>
                  ) : (
                    <span>
                      Found {filteredTakeoffs.length} takeoff{filteredTakeoffs.length !== 1 ? 's' : ''}
                      {debouncedTakeoffSearch && ` matching "${debouncedTakeoffSearch}"`}
                    </span>
                  )}
                </div>
              )}
              {filteredTakeoffs.length > 0 && (
                <div style={{
                  display: 'flex',
                  justifyContent: 'space-between',
                  alignItems: 'center',
                  flexWrap: 'wrap',
                  gap: 'var(--space-3)',
                }}>
                  <div style={{ fontSize: 'var(--text-sm)', color: 'var(--gray-400)' }}>
                    Showing {takeoffShowingStart}-{takeoffShowingEnd} of {filteredTakeoffs.length} takeoffs
                  </div>
                  {takeoffTotalPages > 1 && (
                    <div style={{ display: 'flex', gap: 'var(--space-2)' }}>
                      <Button
                        variant="ghost"
                        size="sm"
                        onClick={() => setTakeoffCurrentPage(takeoffCurrentPage - 1)}
                        disabled={takeoffCurrentPage === 1}
                      >
                        Previous
                      </Button>

                      {/* Page numbers */}
                      {Array.from({ length: Math.min(takeoffTotalPages, 5) }, (_, i) => {
                        let pageNum;
                        if (takeoffTotalPages <= 5) {
                          pageNum = i + 1;
                        } else if (takeoffCurrentPage <= 3) {
                          pageNum = i + 1;
                        } else if (takeoffCurrentPage >= takeoffTotalPages - 2) {
                          pageNum = takeoffTotalPages - 4 + i;
                        } else {
                          pageNum = takeoffCurrentPage - 2 + i;
                        }

                        return (
                          <Button
                            key={pageNum}
                            variant={takeoffCurrentPage === pageNum ? 'secondary' : 'ghost'}
                            size="sm"
                            onClick={() => setTakeoffCurrentPage(pageNum)}
                          >
                            {pageNum}
                          </Button>
                        );
                      })}

                      <Button
                        variant="ghost"
                        size="sm"
                        onClick={() => setTakeoffCurrentPage(takeoffCurrentPage + 1)}
                        disabled={takeoffCurrentPage === takeoffTotalPages}
                      >
                        Next
                      </Button>
                    </div>
                  )}
                </div>
              )}
            </div>
          </div>
        ) : activeTab === "all-jobs" ? (
          <div style={{
            border: '1px solid var(--gray-600)',
            borderRadius: 'var(--radius-lg)',
            overflow: 'hidden',
            display: 'flex',
            flexDirection: 'column',
            maxHeight: '85vh',
          }}>
            {/* Toolbar */}
            <div style={{
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'space-between',
              padding: 'var(--space-4)',
              backgroundColor: 'var(--dark-bg)',
              borderBottom: '1px solid var(--gray-600)',
              gap: 'var(--space-4)',
              flexShrink: 0,
            }}>
              <div style={{ width: '300px', flexShrink: 0 }}>
                <SearchBar
                  value={jobsSearchQuery}
                  onChange={(e) => {
                    updateJobsSearch(e.target.value);
                    setJobsCurrentPage(1);
                  }}
                  placeholder="Search all jobs..."
                  size="sm"
                />
              </div>
              <div style={{ display: 'flex', gap: 'var(--space-3)', alignItems: 'center' }}>
                <Popover
                  isOpen={showJobsFilters}
                  onClose={() => setShowJobsFilters(false)}
                  trigger={
                    <Button
                      variant="ghost"
                      size="sm"
                      onClick={() => {
                        setShowJobsFilters(!showJobsFilters);
                        setShowJobsColumns(false);
                      }}
                    >
                      <Filter size={16} />
                      Filters
                    </Button>
                  }
                  position="bottom-right"
                  width="600px"
                  maxWidth="90vw"
                >
                  <div style={{ display: 'flex', flexDirection: 'column', gap: 'var(--space-4)' }}>
                    <h3 style={{
                      margin: 0,
                      fontSize: 'var(--text-base)',
                      fontWeight: 'var(--font-semibold)',
                      color: 'var(--gray-200)'
                    }}>
                      Filter Jobs
                    </h3>

                    {/* Modeling Status Filter */}
                    <MultiSelectDropdown
                      label="Modeling Status"
                      options={modelingStatusOptions || []}
                      selected={jobsModelingStatusFilter}
                      onChange={(value) => {
                        setJobsModelingStatusFilter(value);
                        setJobsCurrentPage(1);
                      }}
                      placeholder="All Statuses"
                    />

                    {/* Agreement Status Filter */}
                    <MultiSelectDropdown
                      label="Agreement Status"
                      options={agreementStatusOptions || []}
                      selected={jobsAgreementStatusFilter}
                      onChange={(value) => {
                        setJobsAgreementStatusFilter(value);
                        setJobsCurrentPage(1);
                      }}
                      placeholder="All Statuses"
                    />

                    {/* Billing Status Filter */}
                    <MultiSelectDropdown
                      label="Billing Status"
                      options={billingStatusOptions || []}
                      selected={jobsBillingStatusFilter}
                      onChange={(value) => {
                        setJobsBillingStatusFilter(value);
                        setJobsCurrentPage(1);
                      }}
                      placeholder="All Statuses"
                    />

                    {/* Stage Filter */}
                    <MultiSelectDropdown
                      label="Stage"
                      options={stageStatusOptions || []}
                      selected={jobsStageStatusFilter}
                      onChange={(value) => {
                        setJobsStageStatusFilter(value);
                        setJobsCurrentPage(1);
                      }}
                      placeholder="All Stages"
                    />

                    {/* Client Filter */}
                    <MultiSelectDropdown
                      label="Client"
                      options={clientOptions || []}
                      selected={jobsClientFilter}
                      onChange={(value) => {
                        setJobsClientFilter(value);
                        setJobsCurrentPage(1);
                      }}
                      placeholder="All Clients"
                    />

                    {/* Footer Actions */}
                    <div style={{
                      display: 'flex',
                      justifyContent: 'space-between',
                      paddingTop: 'var(--space-3)',
                      borderTop: '1px solid var(--gray-700)'
                    }}>
                      <Button
                        variant="ghost"
                        size="sm"
                        onClick={() => {
                          clearJobsSearch();
                          setJobsModelingStatusFilter([]);
                          setJobsAgreementStatusFilter([]);
                          setJobsBillingStatusFilter([]);
                          setJobsStageStatusFilter([]);
                          setJobsClientFilter([]);
                          setJobsCurrentPage(1);
                        }}
                      >
                        Clear all
                      </Button>
                      <Button
                        variant="primary"
                        size="sm"
                        onClick={() => setShowJobsFilters(false)}
                      >
                        Apply
                      </Button>
                    </div>
                  </div>
                </Popover>

                <Popover
                  isOpen={showJobsColumns}
                  onClose={() => setShowJobsColumns(false)}
                  trigger={
                    <Button
                      variant="ghost"
                      size="sm"
                      onClick={() => {
                        setShowJobsColumns(!showJobsColumns);
                        setShowJobsFilters(false);
                      }}
                    >
                      <Settings size={16} />
                      Columns
                    </Button>
                  }
                  position="bottom-right"
                  width="300px"
                  maxWidth="90vw"
                >
                  <div style={{ display: 'flex', flexDirection: 'column', gap: 'var(--space-4)' }}>
                    <h3 style={{
                      margin: 0,
                      fontSize: 'var(--text-base)',
                      fontWeight: 'var(--font-semibold)',
                      color: 'var(--gray-200)'
                    }}>
                      Toggle Columns
                    </h3>

                    {/* Column checkboxes */}
                    <div style={{ display: 'flex', flexDirection: 'column', gap: 'var(--space-2)', maxHeight: '400px', overflowY: 'auto' }}>
                      {[
                        { key: "action", label: "View Button" },
                        { key: "jobNumber", label: "Job Number" },
                        { key: "projectName", label: "Project Name" },
                        { key: "clientName", label: "Client" },
                        { key: "contactName", label: "Contact" },
                        { key: "agreementStatus", label: "Agreement Status" },
                        { key: "billingStatus", label: "Billing Status" },
                        { key: "readyToInvoice", label: "Ready to Invoice" },
                        { key: "modelPrice", label: "Model Price" },
                        { key: "calculatedPrice", label: "Calculated Price" },
                        { key: "poNumber", label: "PO Number" },
                        { key: "initialAcceptance", label: "Initial Acceptance" },
                        { key: "invoicedDate", label: "Invoiced Date" },
                        { key: "paidDate", label: "Paid Date" },
                        { key: "address", label: "Address" },
                        { key: "modelingStatus", label: "Modeling Status" },
                      ].map((col) => (
                        <label
                          key={col.key}
                          style={{
                            display: 'flex',
                            alignItems: 'center',
                            gap: 'var(--space-2)',
                            padding: 'var(--space-2)',
                            cursor: 'pointer',
                            borderRadius: 'var(--radius-md)',
                            transition: 'background-color var(--transition-fast)',
                          }}
                          onMouseEnter={(e) => e.currentTarget.style.backgroundColor = 'var(--dark-elevated)'}
                          onMouseLeave={(e) => e.currentTarget.style.backgroundColor = 'transparent'}
                        >
                          <input
                            type="checkbox"
                            checked={jobsVisibleColumns[col.key] !== false}
                            onChange={() => handleJobsColumnToggle(col.key)}
                            style={{ cursor: 'pointer' }}
                          />
                          <span style={{ fontSize: 'var(--text-sm)', color: 'var(--gray-200)' }}>
                            {col.label}
                          </span>
                        </label>
                      ))}
                    </div>
                  </div>
                </Popover>

                <Button
                  variant={jobsAutoResize ? 'secondary' : 'ghost'}
                  size="sm"
                  onClick={() => setJobsAutoResize(!jobsAutoResize)}
                >
                  {jobsAutoResize ? '✓ ' : ''}Auto-resize
                </Button>
              </div>
            </div>
            {/* Scrollable Table Container */}
            <div style={{
              backgroundColor: 'var(--dark-surface)',
              overflowX: 'auto',
              overflowY: 'auto',
              flex: 1,
              minWidth: 0,
            }}>
              <AllJobsTable
                projects={paginatedJobs}
                contacts={contacts}
                onRowClick={(project) => {
                  setSelectedProject(project);
                  setModalType("project");
                  setModalOpen(true);
                }}
                onProjectUpdate={() => loadAllData(true)}
                visibleColumns={jobsVisibleColumns}
                autoResizeEnabled={jobsAutoResize}
              />
            </div>

            {/* Search Results and Pagination Controls */}
            <div style={{
              display: 'flex',
              flexDirection: 'column',
              gap: 'var(--space-3)',
              padding: 'var(--space-4)',
              backgroundColor: 'var(--dark-bg)',
              borderTop: '1px solid var(--gray-600)',
              flexShrink: 0,
            }}>
              {debouncedJobsSearch && (
                <div style={{
                  fontSize: 'var(--text-sm)',
                  color: 'var(--gray-400)',
                }}>
                  {isJobsSearching ? (
                    <span>Searching...</span>
                  ) : (
                    <span>
                      Found {filteredJobs.length} job{filteredJobs.length !== 1 ? 's' : ''}
                      {debouncedJobsSearch && ` matching "${debouncedJobsSearch}"`}
                    </span>
                  )}
                </div>
              )}
              {filteredJobs.length > 0 && (
                <div style={{
                  display: 'flex',
                  justifyContent: 'space-between',
                  alignItems: 'center',
                  flexWrap: 'wrap',
                  gap: 'var(--space-3)',
                }}>
                  <div style={{ fontSize: 'var(--text-sm)', color: 'var(--gray-400)' }}>
                    Showing {jobsShowingStart}-{jobsShowingEnd} of {filteredJobs.length} jobs
                  </div>
                  {jobsTotalPages > 1 && (
                    <div style={{ display: 'flex', gap: 'var(--space-2)' }}>
                      <Button
                        variant="ghost"
                        size="sm"
                        onClick={() => setJobsCurrentPage(jobsCurrentPage - 1)}
                        disabled={jobsCurrentPage === 1}
                      >
                        Previous
                      </Button>

                      {/* Page numbers */}
                      {Array.from({ length: Math.min(jobsTotalPages, 5) }, (_, i) => {
                        let pageNum;
                        if (jobsTotalPages <= 5) {
                          pageNum = i + 1;
                        } else if (jobsCurrentPage <= 3) {
                          pageNum = i + 1;
                        } else if (jobsCurrentPage >= jobsTotalPages - 2) {
                          pageNum = jobsTotalPages - 4 + i;
                        } else {
                          pageNum = jobsCurrentPage - 2 + i;
                        }

                        return (
                          <Button
                            key={pageNum}
                            variant={jobsCurrentPage === pageNum ? 'secondary' : 'ghost'}
                            size="sm"
                            onClick={() => setJobsCurrentPage(pageNum)}
                          >
                            {pageNum}
                          </Button>
                        );
                      })}

                      <Button
                        variant="ghost"
                        size="sm"
                        onClick={() => setJobsCurrentPage(jobsCurrentPage + 1)}
                        disabled={jobsCurrentPage === jobsTotalPages}
                      >
                        Next
                      </Button>
                    </div>
                  )}
                </div>
              )}
            </div>
          </div>
        ) : null}
      </div>

      {modalOpen &&
        modalType === "project" &&
        selectedProject &&
        modalSource === "agreementReady" && (
          <AgreementReadyModal
            project={selectedProject}
            clients={clientsWithBilling}
            contacts={contacts}
            employees={employees}
            isOpen={modalOpen}
            onClose={handleModalClose}
            onSave={handleModalSave}
            theme={theme}
          />
        )}

      {modalOpen &&
        modalType === "project" &&
        selectedProject &&
        modalSource !== "agreementReady" && (
          <OfficeAdminModal
            project={selectedProject}
            clients={clients}
            contacts={contacts}
            employees={employees}
            isOpen={modalOpen}
            onClose={handleModalClose}
            onSave={handleModalSave}
            theme={theme}
          />
        )}

      {modalOpen && modalType === "client" && selectedClient && (
        <ClientModal
          client={selectedClient}
          isOpen={modalOpen}
          onClose={handleModalClose}
          onSave={handleModalSave}
          theme={theme}
        />
      )}

      {modalOpen && modalType === "takeoff" && selectedTakeoff && (
        <TakeoffManagementModal
          takeoff={selectedTakeoff}
          isOpen={modalOpen}
          onClose={handleModalClose}
          onSave={handleModalSave}
          clients={clients}
          theme={theme}
        />
      )}

      <TakeoffManagementModal
        takeoff={selectedTakeoffManagement}
        isOpen={isTakeoffManagementModalOpen}
        onClose={() => setIsTakeoffManagementModalOpen(false)}
        onSave={() => loadAllData(true)}
        clients={clients}
        theme={theme}
      />
    </div>
  );
};

export default OfficeAdminDashboard;
